import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import { formatApiErrorDetail } from "@/api";

const DEMO = [
  { label: "Admin Lala", email: "admin@lala.com", password: "LalaAdmin2026!" },
  { label: "Evaluador", email: "evaluator@lala.com", password: "Eval2026!" },
  { label: "Stakeholder", email: "stakeholder@lala.com", password: "Stake2026!" },
  { label: "Proveedor · Lácteos del Bajío", email: "supplier@bajio.mx", password: "Prov2026!" },
  { label: "Equipo TI (demo)", email: "tech_demo@lala.com", password: "DemoTI2026!" },
];

const SSO_ERROR_MESSAGES = {
  user_not_provisioned:
    "Tu cuenta Microsoft es válida, pero aún no tienes acceso a la plataforma. Solicita acceso a tu administrador.",
  sso_cancelled: "Cancelaste el inicio de sesión con Microsoft.",
  session_expired: "La sesión de inicio expiró. Inténtalo de nuevo.",
  sso_failed: "No pudimos completar el inicio de sesión con Microsoft. Inténtalo de nuevo.",
};

export default function Login() {
  const { login, loginWithEntra } = useAuth();  // Section 6.1: loginWithEntra
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [ssoErr, setSsoErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSsoErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      const defaultLanding =
        u.role === "supplier" ? "/mi-evaluacion" :
          u.role === "owner" ? "/dev" :
            u.role === "buyer" ? "/comprador" :
              u.role === "tech_admin" ? "/portal-ti" :
                "/dashboard";
      const redirect = loc.state?.from || defaultLanding;
      nav(redirect);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Error al iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  // Section 6.1: Handle Entra SSO login
  const handleEntraLogin = (e) => {
    e.preventDefault();
    setErr(""); setSsoErr("");
    loginWithEntra();  // Redirects to backend /api/auth/entra/login
  };

  useEffect(() => {
    const code = new URLSearchParams(loc.search).get("error");
    if (code) {
      setSsoErr(SSO_ERROR_MESSAGES[code] ?? SSO_ERROR_MESSAGES.sso_failed);
      nav(loc.pathname, { replace: true, state: loc.state });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="paper min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-10 items-center">
        <div
          className="hidden md:block rounded-3xl p-8 text-white relative overflow-hidden"
          style={{ backgroundColor: "var(--lala-blue)", backgroundImage: "url(/brand/mariposa_azul_bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
          data-testid="login-brand-panel"
        >
          <img
            src="/brand/lala_elite_badge_md.png"
            alt="Lala Elite"
            className="h-32 w-32 object-contain mb-6"
            draggable={false}
          />
          <div className="font-hand text-2xl" style={{ color: "#fff", opacity: 0.9 }}>bienvenido a la familia Elite</div>
          <h1 className="font-display text-5xl mt-2 leading-tight text-white">Crecemos juntos</h1>
          <p className="text-white/85 mt-4 max-w-md">
            Accede para consultar tu desempeño dentro de la familia LALA Elite, descargar tu sello digital o
            acompañar el crecimiento de los proveedores estratégicos de Grupo Lala.
          </p>
          {window.location.hostname.includes("preview") && (
            <div className="mt-8 card-silver p-5">
              <div className="font-hand text-xl text-[var(--lala-blue)] mb-2">cuentas demo</div>
              <div className="grid gap-2 text-xs">
                {DEMO.map((d) => (
                  <button
                    key={d.email}
                    onClick={() => { setEmail(d.email); setPassword(d.password); }}
                    data-testid={`demo-${d.label.replace(/\s+/g, '-').toLowerCase()}`}
                    className="text-left px-3 py-2 rounded-lg hover:bg-[var(--lala-blue-soft)] flex justify-between items-center gap-2"
                  >
                    <div>
                      <div className="font-semibold text-[var(--ink)]">{d.label}</div>
                      <div className="text-[var(--ink-soft)]">{d.email}</div>
                    </div>
                    <span className="font-hand text-[var(--lala-blue)]">usar →</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="card-silver p-8" data-testid="login-form">
          <h2 className="font-display text-3xl text-[var(--lala-blue)]">Iniciar sesión</h2>
          <p className="text-sm text-[var(--ink-soft)] mt-1">Accede a tu cuenta Lala Elite</p>

          {/* Section 6.1: Entra SSO Button */}
          {process.env.REACT_APP_ENABLE_ENTRA_LOGIN === "true" && (
            <>

              {ssoErr && (
                <div data-testid="login-sso-error" role="alert"
                  className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                  {ssoErr}
                </div>
              )}

              <button
                type="button"
                onClick={handleEntraLogin}
                data-testid="login-entra-button"
                className="btn-primary w-full mt-6 justify-center bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600"
              >
                Ingresar con cuenta Microsoft
              </button>

              {/* Divider */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-xs text-[var(--ink-soft)] font-semibold">O</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
            </>
          )}

          <label className="block mt-6 text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-field mt-1" placeholder="tu@empresa.com" />

          <label className="block mt-4 text-xs font-semibold uppercase tracking-wider text-[var(--ink-soft)]">Contraseña</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-field mt-1" placeholder="••••••••" />

          {err && <div data-testid="login-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

          <button type="submit" data-testid="login-submit" disabled={busy} className="btn-primary w-full mt-6 justify-center">
            {busy ? "Entrando…" : "Entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}
